import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import _ from "lodash";
import axios from "axios";
import MUIDataTable from "mui-datatables";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import CustomFooter from "./CustomFooter";

import {
  updateData,
  totalDocCount,
  updateFilterField,
} from "../redux/actions";

const theme = createMuiTheme({
  palette: {
    primary: { main: "#091F2F" },
    secondary: { main: "#1871BD", contrastText: "#ffffff" },
  },
  typography: {
    fontFamily: "-Lora-Regular, sans-serif",
  },
  overrides: {
    MUIDataTableHeadCell: {
      fixedHeader: {
        fontFamily: "-Montserrat-Bold, sans-serif",
      },
      data: {
        fontWeight: "600",
        textTransform: "uppercase",
      },
    },
  },
});

const styles = (theme) => ({
  root: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  language: {
    width: "85%",
    color: "#091F2f",
    margin: "0 auto",
    padding: 10,
    [theme.breakpoints.down("sm")]: {
      display: "none", // Hide on small screens (tablet & mobile)
    },
  },
});

class TableView extends React.Component {
  componentDidMount() {
      this.fetchdata();
  }

  componentDidUpdate(prevProps) {
    const filtersChanged = !_.isEqual(this.props.filters, prevProps.filters);
    if (filtersChanged) {
      this.fetchdata();
    }
  }

  fetchdata = () => {
    const {
      docTypeValue,
      projId,
      neighborhoodValue,
      searchText,
      startDate,
      endDate,
      page,
      pageMarker,
    } = this.props.filters;

    const { allWebURL } = this.props;
    const currentMarker = _.get(pageMarker, page - 1, "");
    const filterString = `ft_docType=${docTypeValue}&ft_projID=${projId}&ft_neighborhood=${neighborhoodValue}&ft_Search=${searchText}&ft_startDate=${startDate}&ft_endDate=${endDate}&ft_page=${page}&ft_next_marker=${currentMarker}`;
    this.props.dispatch(updateFilterField("showProgress", true));

    axios
      .get(`https://sire.bostonplans.org/api/documentSearch/getMetadataWithProjects?${filterString}`)
      .then((results) => {
        let tableListData = [];

        if (results.data.totalcount !== 0) {
          results.data.metadataObj.forEach((record) => {
            const box_root = record.boxMetadata;
            let projName = _.get(box_root, "projectName", "Project Name Not Found");
            let projLink = `http://www.bostonplans.org/projects/development-projects/?search=${projName}`;

            const match = allWebURL.find((url) => url.id === box_root.id);
            if (match) projLink = match.url;

            const docSubType = box_root.subtype;
            const docLink = _.get(record, "shareLink", "");
            const neighborhood = box_root.neighborhood;
            const documentDate = box_root.documentDate
              ? box_root.documentDate.substring(0, 10)
              : "Date Not Available";

            tableListData.push([
              projName,
              projLink,
              neighborhood,
              docSubType,
              docLink,
              documentDate,
            ]);
          });

          this.props.dispatch(updateData(tableListData));

          if (results.data.next_marker !== "") {
            const updatedMarkers = {
              ...pageMarker,
              [page]: results.data.next_marker,
            };
            this.props.dispatch(updateFilterField("page", page));
            this.props.dispatch(updateFilterField("pageMarker", updatedMarkers));
          }

          this.props.dispatch(totalDocCount(results.data.totalcount));
        } else {
          this.props.dispatch(updateData([]));
          this.props.dispatch(totalDocCount(0));
        }
      });
  };

  render() {
    const { classes, data, filters, totalDocCount, showProgress } = this.props;

    const handleCellClick = (cellData) => {
      if (cellData?.props?.href) {
        window.open(cellData.props.href, "_blank");
      }
    };

    const options = {
      filter: false,
      filterType: "dropdown",
      responsive: "vertical",
      selectableRows: "none",
      rowsPerPage: 50,
      onCellClick: handleCellClick,
      customFooter: (
        count,
        page,
        rowsPerPage,
        changeRowsPerPage,
        changePage,
        textLabels
      ) => (
        <CustomFooter
          count={count}
          page={page}
          onPageChange={changePage}
          textLabels={textLabels}
        />
      ),
      customSort: (data, colIndex, order) => {
        return data.sort((a, b) => {
          const valA = a.data[colIndex];
          const valB = b.data[colIndex];
          const valueA = Array.isArray(valA) ? valA[0] : valA;
          const valueB = Array.isArray(valB) ? valB[0] : valB;
          return (valueA < valueB ? -1 : 1) * (order === "asc" ? 1 : -1);
        });
      },
      textLabels: {
        body: {
          noMatch: "No results. Try adjusting your filters.",
        },
      },
    };

    const formated_data = (data || []).map((d) => [
      [`${d[0]}`, `${d[1]}`],
      `${d[2]}`,
      [`${d[3]}`, `${d[4]}`],
      `${d[5]}`,
    ]);

    if (showProgress) {
      return (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h4>Fetching updated results…</h4>
        </div>
      );
    }

    return (
      <MuiThemeProvider theme={theme}>
        <div className={classes.root}>
          <div className={classes.language}>
            Article 80 development project documents from 2015 to the present are available below. Find Article 80 development project documents from before 2015 in the{" "}
            <a href="http://www.bostonplans.org/document-center">Document Center</a>. We are working backwards to update this library and documents dated prior to 2015 will be available here as soon as possible. In addition, find all non-Article 80 documents—including planning, research, maps, policy, annual reports and more—in the{" "}
            <a href="http://www.bostonplans.org/document-center">Document Center</a>. Do you have a question or need assistance? Please{" "}
            <a href="http://www.bostonplans.org/about-us/contact-us">contact us</a>.
          </div>

          <MUIDataTable
            data={formated_data}
            columns={[
              {
                name: "Project Name",
                options: {
                  customBodyRender: (value) => (
                    <a href={value[1]} target="_blank" rel="noopener noreferrer">
                      {value[0]}
                    </a>
                  ),
                },
              },
              { name: "Neighborhood" },
              {
                name: "Document Type",
                options: {
                  customBodyRender: (value) => (
                    <a href={value[1]} target="_blank" rel="noopener noreferrer">
                      {value[0]}
                    </a>
                  ),
                },
              },
              { name: "Date" },
            ]}
            options={options}
          />
        </div>
      </MuiThemeProvider>
    );
  }
}

TableView.propTypes = {
  classes: PropTypes.object,
  filters: PropTypes.object.isRequired,
  data: PropTypes.array,
  pageMarker: PropTypes.object.isRequired,
  allWebURL: PropTypes.array.isRequired,
  totalDocCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showProgress: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  filters: state.filters,
  data: state.data,
  pageMarker: state.filters.pageMarker,
  page: state.filters.page,
  allWebURL: state.allWebURL,
  totalDocCount: state.totalDocCount,
  showProgress: state.showProgress,
});

export default withStyles(styles)(connect(mapStateToProps)(TableView));
